import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarPlus, Plus } from 'lucide-react';

import { api } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { requirePermission } from '@/lib/route-guards';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { PlanDialog } from '@/components/maintenance/PlanDialog';
import { WorkOrderDialog } from '@/components/maintenance/WorkOrderDialog';
import { QueryError } from '@/components/QueryError';
import { DUE_BADGE, PLAN_TYPE_LABELS, dueState, fmtDay, fmtKm, nextDue } from '@/lib/maintenance';
import {
  WorkOrderDetailDialog, WO_STATUS_LABELS, WO_STATUS_VARIANT,
} from '@/components/maintenance/WorkOrderDetailDialog';

import type { MaintenancePlan, WorkOrder, Paginated } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/maintenance')({
  beforeLoad: () => requirePermission('maintenance.read'),
  component: MaintenancePage,
});

function MaintenancePage() {
  const [planOpen, setPlanOpen] = useState(false);
  const [woOpen, setWoOpen] = useState(false);
  const [woFromPlan, setWoFromPlan] = useState<MaintenancePlan | undefined>();
  const [detailId, setDetailId] = useState<string | null>(null);
  const canWrite = useHasPermission('maintenance.write');
  const plansQuery = useQuery({
    queryKey: ['maintenance-plans'],
    queryFn: async () => (await api.get<Paginated<MaintenancePlan>>('/maintenance/plans?limit=200')).data,
  });
  const { data: plans, isLoading: plansLoading } = plansQuery;

  const ordersQuery = useQuery({
    queryKey: ['work-orders'],
    queryFn: async () => (await api.get<Paginated<WorkOrder>>('/work-orders?limit=200')).data,
  });
  const { data: orders, isLoading: ordersLoading } = ordersQuery;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Maintenance</h1>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setPlanOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Plan préventif
            </Button>
            <Button onClick={() => { setWoFromPlan(undefined); setWoOpen(true); }}>
              <CalendarPlus className="h-4 w-4 mr-2" /> Planifier une intervention
            </Button>
          </div>
        )}
      </div>
      <QueryError query={[plansQuery, ordersQuery]} />
      <Tabs defaultValue="plans">
        <TabsList>
          <TabsTrigger value="plans">Plans préventifs</TabsTrigger>
          <TabsTrigger value="orders">Ordres de travail</TabsTrigger>
        </TabsList>

        <TabsContent value="plans">
          <div className="bg-card rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Plan</TableHead>
                  <TableHead>Véhicule</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Intervalle</TableHead>
                  <TableHead>Prochaine échéance</TableHead>
                  <TableHead>État</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {plansLoading && Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 7 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
                ))}
                {plans?.data?.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      {p.name}
                      {!p.active && <Badge variant="secondary" className="ml-2">Inactif</Badge>}
                    </TableCell>
                    <TableCell>{p.vehicle?.plate ?? '—'}</TableCell>
                    <TableCell>{PLAN_TYPE_LABELS[p.type] ?? p.type}</TableCell>
                    <TableCell>
                      {[fmtKm(p.intervalKm), p.intervalDays ? `${p.intervalDays} j` : null, p.intervalHours ? `${p.intervalHours} h` : null]
                        .filter(Boolean).join(' · ') || '—'}
                    </TableCell>
                    <TableCell>{nextDue(p)}</TableCell>
                    <TableCell>
                      <Badge variant={DUE_BADGE[dueState(p)].variant}>{DUE_BADGE[dueState(p)].label}</Badge>
                    </TableCell>
                    <TableCell>
                      {canWrite && (
                        <Button size="sm" variant="ghost" onClick={() => { setWoFromPlan(p); setWoOpen(true); }}>
                          Planifier maintenant
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {plans && (plans.data?.length ?? 0) === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Aucun plan préventif — « Plan préventif » pour en créer un</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="bg-card rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Véhicule</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Coût</TableHead>
                  <TableHead>Prévu le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordersLoading && Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
                ))}
                {orders?.data?.map((o) => (
                  <TableRow key={o.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setDetailId(o.id)}>
                    <TableCell className="font-mono">{o.reference}</TableCell>
                    <TableCell>{o.vehicle?.plate ?? '—'}</TableCell>
                    <TableCell>{o.description ?? o.plan?.name ?? '—'}</TableCell>
                    <TableCell>
                      <Badge variant={WO_STATUS_VARIANT[o.status]}>{WO_STATUS_LABELS[o.status] ?? o.status}</Badge>
                    </TableCell>
                    <TableCell>{o.totalCost != null ? `${o.totalCost.toLocaleString('fr-FR')} ${o.currency}` : '—'}</TableCell>
                    <TableCell>{fmtDay(o.scheduledAt) ?? fmtDay(o.createdAt)}</TableCell>
                  </TableRow>
                ))}
                {orders && (orders.data?.length ?? 0) === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Aucun ordre de travail</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <PlanDialog open={planOpen} onOpenChange={setPlanOpen} />
      <WorkOrderDialog open={woOpen} onOpenChange={setWoOpen} fromPlan={woFromPlan} />
      <WorkOrderDetailDialog workOrderId={detailId} onOpenChange={(o) => { if (!o) setDetailId(null); }} />
    </div>
  );
}
